// Firebase Leaderboard System
// Handles leaderboard creation, management, and real-time updates for completed events

import { 
  doc, 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  writeBatch,
  runTransaction,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { COLLECTIONS } from './collections';

// ===== LEADERBOARD MANAGEMENT =====

// Create a new leaderboard
export const createLeaderboard = async (leaderboardData) => {
  try {
    const leaderboard = {
      ...leaderboardData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isPublished: false,
      totalParticipants: 0
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.ACTIVITY_LEADERBOARDS), leaderboard);
    return { success: true, leaderboardId: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update leaderboard
export const updateLeaderboard = async (leaderboardId, updateData) => {
  try {
    const leaderboardRef = doc(db, COLLECTIONS.ACTIVITY_LEADERBOARDS, leaderboardId);
    await updateDoc(leaderboardRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Delete leaderboard
export const deleteLeaderboard = async (leaderboardId) => {
  try {
    const leaderboardRef = doc(db, COLLECTIONS.ACTIVITY_LEADERBOARDS, leaderboardId);
    await deleteDoc(leaderboardRef);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get leaderboard by ID
export const getLeaderboard = async (leaderboardId) => {
  try {
    const leaderboardRef = doc(db, COLLECTIONS.ACTIVITY_LEADERBOARDS, leaderboardId);
    const leaderboardSnap = await getDoc(leaderboardRef);
    
    if (leaderboardSnap.exists()) {
      return { success: true, data: { id: leaderboardSnap.id, ...leaderboardSnap.data() } };
    } else {
      return { success: false, error: 'Leaderboard not found' };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get all leaderboards
export const getAllLeaderboards = async () => {
  try {
    const leaderboardsQuery = query(
      collection(db, COLLECTIONS.ACTIVITY_LEADERBOARDS),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(leaderboardsQuery);
    
    const leaderboards = [];
    snapshot.forEach((doc) => {
      leaderboards.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: leaderboards };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get published leaderboards only
export const getPublishedLeaderboards = async () => {
  try {
    const leaderboardsQuery = query(
      collection(db, COLLECTIONS.ACTIVITY_LEADERBOARDS),
      where('isPublished', '==', true),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(leaderboardsQuery);
    
    const leaderboards = [];
    snapshot.forEach((doc) => {
      leaderboards.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: leaderboards };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get leaderboards by activity registration ID
export const getLeaderboardsByActivity = async (registrationId) => {
  try {
    const leaderboardsQuery = query(
      collection(db, COLLECTIONS.ACTIVITY_LEADERBOARDS),
      where('registrationId', '==', registrationId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(leaderboardsQuery);
    
    const leaderboards = [];
    snapshot.forEach((doc) => {
      leaderboards.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: leaderboards };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== REAL-TIME LEADERBOARD LISTENERS =====

// Listen to leaderboard changes
export const listenToLeaderboard = (leaderboardId, callback) => {
  const leaderboardRef = doc(db, COLLECTIONS.ACTIVITY_LEADERBOARDS, leaderboardId);
  
  const unsubscribe = onSnapshot(leaderboardRef, (doc) => {
    if (doc.exists()) {
      callback({ success: true, data: { id: doc.id, ...doc.data() } });
    } else {
      callback({ success: false, error: 'Leaderboard not found' });
    }
  }, (error) => {
    callback({ success: false, error: error.message });
  });

  return unsubscribe;
};

// Listen to all leaderboards
export const listenToAllLeaderboards = (callback) => {
  const leaderboardsQuery = query(
    collection(db, COLLECTIONS.ACTIVITY_LEADERBOARDS),
    orderBy('createdAt', 'desc')
  );

  const unsubscribe = onSnapshot(leaderboardsQuery, (snapshot) => {
    const leaderboards = [];
    snapshot.forEach((doc) => {
      leaderboards.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: leaderboards });
  }, (error) => {
    callback({ success: false, error: error.message });
  });

  return unsubscribe;
};

// Listen to published leaderboards only
export const listenToPublishedLeaderboards = (callback) => {
  const leaderboardsQuery = query(
    collection(db, COLLECTIONS.ACTIVITY_LEADERBOARDS),
    where('isPublished', '==', true),
    orderBy('createdAt', 'desc')
  );

  const unsubscribe = onSnapshot(leaderboardsQuery, (snapshot) => {
    const leaderboards = [];
    snapshot.forEach((doc) => {
      leaderboards.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: leaderboards });
  }, (error) => {
    callback({ success: false, error: error.message });
  });

  return unsubscribe;
};

// Listen to leaderboards by activity
export const listenToLeaderboardsByActivity = (registrationId, callback) => {
  const leaderboardsQuery = query(
    collection(db, COLLECTIONS.ACTIVITY_LEADERBOARDS),
    where('registrationId', '==', registrationId),
    orderBy('createdAt', 'desc')
  );

  const unsubscribe = onSnapshot(leaderboardsQuery, (snapshot) => {
    const leaderboards = [];
    snapshot.forEach((doc) => {
      leaderboards.push({ id: doc.id, ...doc.data() });
    });
    callback({ success: true, data: leaderboards });
  }, (error) => {
    callback({ success: false, error: error.message });
  });

  return unsubscribe;
};

// ===== LEADERBOARD PARTICIPANTS MANAGEMENT =====

// Add participant to leaderboard
export const addLeaderboardParticipant = async (leaderboardId, participantData) => {
  try {
    const result = await runTransaction(db, async (transaction) => {
      // Get current leaderboard
      const leaderboardRef = doc(db, COLLECTIONS.ACTIVITY_LEADERBOARDS, leaderboardId);
      const leaderboardSnap = await transaction.get(leaderboardRef);
      
      if (!leaderboardSnap.exists()) {
        throw new Error('Leaderboard not found');
      }
      
      const leaderboardData = leaderboardSnap.data();
      
      // Check if participant already exists
      const existingParticipant = leaderboardData.participants?.find(
        p => p.userId === participantData.userId
      );
      
      if (existingParticipant) {
        throw new Error('Participant already exists in leaderboard');
      }
      
      // Add participant to leaderboard
      const updatedParticipants = [
        ...(leaderboardData.participants || []),
        {
          ...participantData,
          addedAt: serverTimestamp()
        }
      ];
      
      // Sort participants by rank (lowest first)
      updatedParticipants.sort((a, b) => (a.rank || 999) - (b.rank || 999));
      
      // Update leaderboard
      transaction.update(leaderboardRef, {
        participants: updatedParticipants,
        totalParticipants: updatedParticipants.length,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    });

    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Update participant score
export const updateParticipantScore = async (leaderboardId, userId, newScore) => {
  try {
    const result = await runTransaction(db, async (transaction) => {
      const leaderboardRef = doc(db, COLLECTIONS.ACTIVITY_LEADERBOARDS, leaderboardId);
      const leaderboardSnap = await transaction.get(leaderboardRef);
      
      if (!leaderboardSnap.exists()) {
        throw new Error('Leaderboard not found');
      }
      
      const leaderboardData = leaderboardSnap.data();
      const participants = leaderboardData.participants || [];
      
      // Find and update participant
      const updatedParticipants = participants.map(participant => {
        if (participant.userId === userId) {
          return { ...participant, score: newScore, updatedAt: serverTimestamp() };
        }
        return participant;
      });
      
      // Sort participants by rank (lowest first)
      updatedParticipants.sort((a, b) => (a.rank || 999) - (b.rank || 999));
      
      // Update leaderboard
      transaction.update(leaderboardRef, {
        participants: updatedParticipants,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    });

    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Remove participant from leaderboard
export const removeLeaderboardParticipant = async (leaderboardId, userId) => {
  try {
    const result = await runTransaction(db, async (transaction) => {
      const leaderboardRef = doc(db, COLLECTIONS.ACTIVITY_LEADERBOARDS, leaderboardId);
      const leaderboardSnap = await transaction.get(leaderboardRef);
      
      if (!leaderboardSnap.exists()) {
        throw new Error('Leaderboard not found');
      }
      
      const leaderboardData = leaderboardSnap.data();
      const participants = leaderboardData.participants || [];
      
      // Remove participant
      const updatedParticipants = participants.filter(
        participant => participant.userId !== userId
      );
      
      // Update leaderboard
      transaction.update(leaderboardRef, {
        participants: updatedParticipants,
        totalParticipants: updatedParticipants.length,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    });

    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== ADMIN FUNCTIONS =====

// Publish leaderboard (admin only)
export const publishLeaderboard = async (leaderboardId) => {
  try {
    const leaderboardRef = doc(db, COLLECTIONS.ACTIVITY_LEADERBOARDS, leaderboardId);
    await updateDoc(leaderboardRef, {
      isPublished: true,
      publishedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Unpublish leaderboard (admin only)
export const unpublishLeaderboard = async (leaderboardId) => {
  try {
    const leaderboardRef = doc(db, COLLECTIONS.ACTIVITY_LEADERBOARDS, leaderboardId);
    await updateDoc(leaderboardRef, {
      isPublished: false,
      unpublishedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ===== UTILITY FUNCTIONS =====

// Get leaderboard statistics
export const getLeaderboardStats = async (leaderboardId) => {
  try {
    const leaderboardRef = doc(db, COLLECTIONS.ACTIVITY_LEADERBOARDS, leaderboardId);
    const leaderboardSnap = await getDoc(leaderboardRef);
    
    if (!leaderboardSnap.exists()) {
      return { success: false, error: 'Leaderboard not found' };
    }
    
    const leaderboardData = leaderboardSnap.data();
    const participants = leaderboardData.participants || [];
    
    // Calculate statistics
    const ranks = participants.map(p => p.rank || 999);
    const stats = {
      totalParticipants: participants.length,
      topRanks: participants.filter(p => p.rank <= 3).length,
      winners: participants.filter(p => p.rank === 1).length,
      averageRank: ranks.length > 0 ? ranks.reduce((a, b) => a + b, 0) / ranks.length : 0,
      isPublished: leaderboardData.isPublished,
      createdAt: leaderboardData.createdAt,
      publishedAt: leaderboardData.publishedAt
    };
    
    return { success: true, data: stats };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Check if leaderboard is published
export const isLeaderboardPublished = (leaderboard) => {
  return leaderboard.isPublished === true;
};

// Get top participants
export const getTopParticipants = (leaderboard, limit = 10) => {
  if (!leaderboard.participants) return [];
  
  return leaderboard.participants
    .sort((a, b) => (a.rank || 999) - (b.rank || 999))
    .slice(0, limit);
};

export default {
  // Leaderboard management
  createLeaderboard,
  updateLeaderboard,
  deleteLeaderboard,
  getLeaderboard,
  getAllLeaderboards,
  getPublishedLeaderboards,
  getLeaderboardsByActivity,
  
  // Real-time listeners
  listenToLeaderboard,
  listenToAllLeaderboards,
  listenToPublishedLeaderboards,
  listenToLeaderboardsByActivity,
  
  // Participant management
  addLeaderboardParticipant,
  updateParticipantScore,
  removeLeaderboardParticipant,
  
  // Admin functions
  publishLeaderboard,
  unpublishLeaderboard,
  
  // Utilities
  getLeaderboardStats,
  isLeaderboardPublished,
  getTopParticipants
};
